import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary — cattura errori React runtime che altrimenti causerebbero uno
 * schermo bianco (unhandled render/effect exceptions). Sostituisce con una UI
 * di fallback minimale e chiara, con opzione di reload.
 *
 * Uso: <ErrorBoundary><ChildTree /></ErrorBoundary>
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log via console in prod è comunque utile per i devtools
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="error-boundary">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-secondary/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-heading font-bold text-2xl mb-3">Ops, qualcosa è andato storto</h1>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Abbiamo incontrato un errore imprevisto. Riprova ricaricando la pagina.
              Se il problema persiste, scrivici a <a href="mailto:info@tuotax.it" className="text-primary font-semibold">info@tuotax.it</a>.
            </p>
            {this.state.error?.message && (
              <details className="mb-6 text-left">
                <summary className="text-xs text-muted-foreground cursor-pointer">Dettagli tecnici</summary>
                <pre className="text-[10px] bg-muted p-3 rounded-lg mt-2 overflow-x-auto whitespace-pre-wrap">
                  {String(this.state.error.message)}
                </pre>
              </details>
            )}
            <Button onClick={this.handleReload} className="rounded-full h-11 px-6" data-testid="error-boundary-reload">
              <RefreshCw className="w-4 h-4 mr-2" /> Ricarica la pagina
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
